// P13 — Modal admin que carrega o conteúdo COMPLETO de uma conversa.
// Decisão G5: admin vê mensagens (user + assistant) de agentes SECTOR/GLOBAL
// pra fins de qualidade e auditoria. Read-only — não dispara nada na conversa
// do owner.

import { FC, useEffect, useState } from 'react';
import adminAgentsService from '@/services/adminAgents.service';
import { ConversationFullResponse } from '@/types/agents';

interface Props {
  agentId: string;
  conversationId: string;
  onClose: () => void;
}

function fmtDateTime(s: string): string {
  return new Date(s).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

const ConversationViewerModal: FC<Props> = ({ agentId, conversationId, onClose }) => {
  const [data, setData] = useState<ConversationFullResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    adminAgentsService
      .getConversationFull(agentId, conversationId)
      .then((res) => {
        if (alive) setData(res);
      })
      .catch(() => {
        if (alive) setError('Não foi possível carregar a conversa.');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [agentId, conversationId]);

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="conversation-viewer-modal"
    >
      <div
        className="bg-surface-primary border border-surface-border rounded-xl max-w-3xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between p-4 border-b border-surface-border">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-content-primary truncate">
              {data?.conversation.title ?? 'Conversa'}
            </h2>
            <p className="text-xs text-content-tertiary mt-0.5">
              {data?.conversation.agent.name} ·{' '}
              {data?.conversation.created_at && fmtDateTime(data.conversation.created_at)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-content-tertiary hover:text-content-primary px-2"
            aria-label="Fechar"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </header>

        <div className="rounded-none bg-amber-500/10 border-y border-amber-500/30 px-4 py-2 text-xs text-amber-200">
          Conteúdo completo visível ao admin (decisão G5 — fins de qualidade/auditoria).
          Use com responsabilidade.
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-container">
          {loading && <p className="text-sm text-content-tertiary">Carregando…</p>}
          {error && (
            <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2">
              {error}
            </p>
          )}
          {!loading && !error && data && data.messages.length === 0 && (
            <p className="text-sm text-content-tertiary">Conversa sem mensagens.</p>
          )}
          {!loading &&
            !error &&
            data?.messages.map((m) => {
              const isUser = m.role === 'USER';
              const align = isUser ? 'items-end' : 'items-start';
              const bg = isUser
                ? 'bg-accent/20 border-accent/30'
                : 'bg-surface-secondary border-surface-border';
              return (
                <div key={m.id} className={`flex flex-col ${align}`} data-testid={`msg-${m.id}`}>
                  <div
                    className={`max-w-[85%] rounded-lg border ${bg} px-3 py-2 text-sm text-content-primary whitespace-pre-wrap`}
                  >
                    {m.content}
                  </div>
                  <p className="text-2xs text-content-tertiary mt-1">
                    {m.role.toLowerCase()} · {fmtDateTime(m.created_at)}
                    {(m.tokens_in > 0 || m.tokens_out > 0) && (
                      <>
                        {' '}
                        · {m.tokens_in} in / {m.tokens_out} out
                      </>
                    )}
                  </p>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default ConversationViewerModal;
